import {Tab, TabList, TabPanel} from '@ariakit/react'
import styled, {css} from 'styled-components'
import {DialogBody} from '@/components/ui/dialog-atoms/DialogAtoms'

/* min-height:0 is what lets this shrink past its content so the footer stays pinned inside the fixed-height dialog. */
export const StyledScrollableBody = styled(DialogBody)`
    flex: 1;
    min-height: 0;
`

export const StyledTabList = styled(TabList)(
    ({theme: {spacing, palette}}) => css`
        display: flex;
        gap: ${spacing * 2}px;
        padding: 0 ${spacing * 6}px;
        border-bottom: 1px solid ${palette.neutral[300]};
        flex-shrink: 0;
    `
)

/* Ariakit hides the inactive panel with an inline display:none, which outranks this class. */
export const StyledTabPanel = styled(TabPanel)`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`

export const StyledTab = styled(Tab)(
    ({theme: {spacing, palette, typography, transition}}) => css`
        ${typography.textSm};
        font-weight: 500;
        background: none;
        border: none;
        border-bottom: 2px solid transparent;
        padding: ${spacing * 3}px ${spacing * 1}px;
        margin-bottom: -1px;
        color: ${palette.neutral[500]};
        cursor: pointer;
        transition: ${transition};

        &:hover {
            color: ${palette.neutral[800]};
        }

        &[aria-selected='true'] {
            color: ${palette.primary[600]};
            border-bottom-color: ${palette.primary[500]};
        }

        &:focus-visible {
            outline: none;
            color: ${palette.primary[600]};
        }
    `
)

export const StyledForm = styled.form(
    ({theme: {spacing}}) => css`
        display: flex;
        flex-direction: column;
        gap: ${spacing * 5}px;
        flex: 1;
        min-height: 0;
    `
)

export const StyledFieldGroup = styled.div(
    ({theme: {spacing}}) => css`
        display: flex;
        flex-direction: column;
        gap: ${spacing * 1.5}px;
    `
)

export const StyledFieldLabel = styled.label(
    ({theme: {typography, palette}}) => css`
        ${typography.textSm};
        font-weight: 500;
        color: ${palette.neutral[800]};
        display: flex;
        align-items: center;
    `
)

export const StyledRequiredMark = styled.span(
    ({theme: {palette}}) => css`
        color: ${palette.danger[500]};
        margin-left: 2px;
    `
)

export const StyledTextInput = styled.input(
    ({theme: {palette, spacing, typography, transition}}) => css`
        ${typography.textSm};
        width: 100%;
        padding: ${spacing * 2.5}px ${spacing * 3}px;
        border: 1px solid ${palette.neutral[300]};
        border-radius: 8px;
        background: ${palette.neutral.white};
        color: ${palette.neutral[900]};
        transition: ${transition};

        &::placeholder {
            color: ${palette.neutral[400]};
        }

        &:focus {
            outline: none;
            border-color: ${palette.primary[500]};
            box-shadow: 0 0 0 3px ${palette.primary[100]};
        }

        &:disabled {
            background: ${palette.neutral[100]};
            color: ${palette.neutral[500]};
            cursor: not-allowed;
        }
    `
)

export const StyledFieldError = styled.span(
    ({theme: {palette, typography}}) => css`
        ${typography.textXs};
        color: ${palette.danger[600]};
    `
)

export const StyledReporterInfo = styled.div(
    ({theme: {palette, spacing, typography}}) => css`
        ${typography.textSm};
        display: flex;
        flex-direction: column;
        gap: ${spacing}px;
        padding: ${spacing * 3}px;
        background: ${palette.neutral[50]};
        border: 1px solid ${palette.neutral[200]};
        border-radius: 8px;
        color: ${palette.neutral[700]};
    `
)

export const StyledReporterRow = styled.div(
    ({theme: {spacing, palette}}) => css`
        display: flex;
        gap: ${spacing * 2}px;

        & > span:first-child {
            color: ${palette.neutral[500]};
            min-width: 60px;
        }

        & > span:last-child {
            color: ${palette.neutral[900]};
            font-weight: 500;
        }
    `
)

export const StyledSubmitError = styled.div(
    ({theme: {palette, spacing, typography}}) => css`
        ${typography.textSm};
        padding: ${spacing * 3}px;
        background: ${palette.danger[50]};
        border: 1px solid ${palette.danger[200]};
        border-radius: 8px;
        color: ${palette.danger[700]};
    `
)
